import {
  MobileStateMediaItem,
  SaveDataMediaItem,
  StateTaskDataTypes,
} from '@safeguardproperties/corescript-react-npm/dist/types';

export type TaskMediaItem = MobileStateMediaItem;

/**
 * Group saved media (saveData.Media) by their TaskId. Mirrors safeview-mobile's
 * getSavedMediaByTask so task-scoped photos can be looked up per task instance.
 */
export const groupSavedMediaByTask = (
  savedMedia?: Record<string, SaveDataMediaItem>
): Record<string, SaveDataMediaItem[]> => {
  const map: Record<string, SaveDataMediaItem[]> = {};
  if (!savedMedia) {
    return map;
  }
  for (const media of Object.values(savedMedia)) {
    const taskId = media?.TaskId;
    if (!taskId) {
      continue;
    }
    (map[taskId] ??= []).push(media);
  }
  return map;
};

/**
 * Port of safeview-mobile's getTaskMedia. Builds the per-label media tiles for a
 * single task: labels are seeded from the task's media state (List + ByQuestion)
 * and the saved media for that task is overlaid to compute Have counts, the
 * thumbnail, and completion. resolveThumb maps a media GUID to a browser-viewable
 * URL (the mobile app uses a file path; the web app uses the gallery webFileName).
 */
export const getTaskMediaItems = (
  savedMediaByTask: Record<string, SaveDataMediaItem[]>,
  taskState: { [key: string]: StateTaskDataTypes } | undefined,
  taskKey: string,
  resolveThumb?: (guid: string, fallback: string) => string
): MobileStateMediaItem[] => {
  const labelMediaMap: Record<string, MobileStateMediaItem> = {};

  const taskMediaState = taskState?.[taskKey]?.media;
  const mediaList = taskMediaState?.List ?? {};
  const byQuestion = taskMediaState?.ByQuestion ?? {};

  // Labels seeded from the task's media state already have Have/Min/Complete
  // computed by the corescript engine (it counts saved media itself), so we must
  // not re-count them below — only refresh their thumbnail to a web URL.
  const stateLabels = new Set<string>();
  for (const labels of Object.values(byQuestion)) {
    for (const label of labels) {
      const media = mediaList[label];
      if (media?.Label) {
        labelMediaMap[media.Label] = media;
        stateLabels.add(media.Label);
      }
    }
  }

  const taskMedia = savedMediaByTask[taskKey];
  if (taskMedia) {
    for (const m of taskMedia) {
      if (m.TaskId !== taskKey) {
        continue;
      }
      const label = m.Label?.[0];
      if (!label) {
        continue;
      }
      const existing = labelMediaMap[label];
      const thumb = resolveThumb ? resolveThumb(m.Guid, m.Path) : m.Path;

      if (stateLabels.has(label)) {
        // Engine already counted this label; just point the tile thumbnail at
        // the web-viewable URL.
        labelMediaMap[label] = { ...existing, Thumb: thumb };
        continue;
      }

      // Label exists only in saved media (not in task state); count it here.
      const have = (existing?.Have ?? 0) + 1;
      const min = existing?.Min ?? 0;
      labelMediaMap[label] = {
        ...existing,
        Label: label,
        Thumb: thumb,
        Have: have,
        Complete: existing?.Complete ?? have >= min,
      } as MobileStateMediaItem;
    }
  }

  // Fulfilled (complete) labels sort to the right; within each group sort
  // alphabetically by the displayed (translated) label text.
  return Object.values(labelMediaMap).sort((a, b) => {
    const completeDiff = Number(a.Complete) - Number(b.Complete);
    if (completeDiff !== 0) {
      return completeDiff;
    }
    return a.Label.toLanguagePack().localeCompare(b.Label.toLanguagePack());
  });
};
